import { Shape } from '@/services/shape';
import { ResourceKey } from '@/services/shape/attributes';
import { buildAstState } from '..';
import { GlobalConfig } from '@/services/gloabl.config';
import { getAutoImageFormat } from './auto-image-format.utils';
import { addResourceIfNotExists } from './resource.utils';
import { isAstEqual } from './equal.utils';
import { toTranformedBasenames } from '@/hooks';

/**
 * 插入图片或图组类资源，返回每个图片对应的名称集；当图片数量>1时，当做数组处理，否则为图片
 * @param resourceAst
 * @param resources
 * @returns
 */
export const insertImages = (
  resourceAst: Ast,
  resources: Shape['attrs']['resources'],
  options: {
    recolorEnable: boolean;
    imgFormat?: string;
    imageArrayLength?: number;
    scale?: number;
    alpha?: number;
  }
) => {
  const buildForEditor = buildAstState.options.buildFor == 'editor';
  const imageMap: { [resourceName in ResourceKey]?: string } = {};
  const {
    recolorEnable,
    imgFormat: userImgFormat,
    imageArrayLength = 99999999
  } = options;

  const { description, theme, colorMode, colorTable = [] } = buildAstState.data;
  if (!description || !theme || !colorMode) return imageMap;

  const userImageFormat = userImgFormat
    ? userImgFormat
    : description?.imageFormat ?? 'indexed8';

  const resourceNames = Object.keys(resources ?? {}) ?? [];
  resourceNames.forEach((resourceName) => {
    const resourceKey = resourceName as ResourceKey;
    // 当前资源resourceName对应的多配色
    const colorIds =
      buildAstState.data.theme?.colorGroupTable.map((item) => item.id) ?? [];

    // 同一个图层生成的多个配色的元素标签（Image,ImageArray）需要使用同一个name名称
    let resourceAstName = '';
    // 该资源所有配色的资源ast
    const _resourceAsts: Ast[] = [];

    colorIds.forEach((colorId, index) => {
      // 如果是recolor模式下，仅需生成一份资源即可
      if (colorMode == 'recolor' && index > 0) return;

      // 获取经过转换之后的图片src
      const _imgArr = [...(resources?.[resourceKey]?.[colorId] ?? [])];
      const imgArr =
        // 正式打包时需要转换图片src，编辑器内部使用时，不需要
        buildForEditor
          ? _imgArr
          : toTranformedBasenames(_imgArr, {
              scale: options.scale ?? 1,
              alpha: options.alpha ?? 1
            });

      const colorValue =
        theme.colorGroupTable.find((item) => item.id == colorId)?.color ?? '';
      if (!colorValue || !imgArr.length || !colorTable.includes(colorValue))
        return;

      let ast: Ast | undefined;
      const srcs: string[] = [];
      // 多图时，生成数组元素
      if (imgArr.length > 1 || resourceName == 'images') {
        const _imgArr: string[] = imgArr
          .filter((item) => buildForEditor || !!item?.trim())
          .slice(0, imageArrayLength);
        ast = {
          tag: 'ImageArray',
          attrsMap: {
            compressMethod: buildAstState.data.compressMethod
          },
          children: _imgArr.map((img) => ({
            tag: 'Image',
            attrsMap: {
              src: img
            },
            children: []
          }))
        };
        srcs.push(..._imgArr);
      } else if (imgArr.length == 1) {
        // 单图，生成图片元素
        const src = imgArr[0];
        if (!src && !buildForEditor) return;
        ast = {
          tag: 'Image',
          attrsMap: {
            src: src,
            compressMethod: buildAstState.data.compressMethod
          },
          children: []
        };
        srcs.push(src);
      }
      if (!ast) return;

      // 添加格式：需要处理是否需要自动选择或者用户选择
      ast.attrsMap['format'] = GlobalConfig.autoChooseImageFormat
        ? getAutoImageFormat(srcs)
        : userImageFormat;

      // 处理配色问题
      if (colorMode == 'colorGroup') {
        // 当只有一个配色时，不启用
        if (colorTable.length > 1) ast.attrsMap['colorGroup'] = colorValue;
      } else {
        ast.attrsMap['recolorEnable'] = recolorEnable ? 'true' : 'false';
      }
      _resourceAsts.push(ast);
    });

    _resourceAsts.forEach((ast) => {
      if (resourceAstName) {
        ast.attrsMap.name = resourceAstName;
        // 将name放到第一位
        ast.attrsMap = {
          name: resourceAstName,
          ...ast.attrsMap
        };
      }
      // 获取实际插入的ast
      ast = addResourceIfNotExists(
        resourceAst,
        ast,
        () => {
          // 多配色时，需要当所有配色资源都相同时，才能是相同
          const existsAsts: Ast[] = [];
          _resourceAsts.forEach((_ast) => {
            const existsAst = resourceAst.children.find((ast3) =>
              isAstEqual(ast3, _ast)
            );
            if (existsAst) {
              if (
                !existsAsts.length ||
                existsAst?.attrsMap.name == existsAsts[0]?.attrsMap?.name
              ) {
                existsAsts.push(existsAst);
              }
            }
          });
          return existsAsts.length == _resourceAsts.length;
        },
        true
      );

      // 根据多配色逻辑，需要重新修改名称：同一资源多个配色配色使用同一个名称
      // 设置多配色的资源名称
      if (!resourceAstName) resourceAstName = ast.attrsMap.name;
    });
    // 设置返回数据值
    imageMap[resourceKey] = resourceAstName ?? '';
  });

  return imageMap;
};

export const getImageSrcs = (watchfaceAst: Ast) => {
  const basenames = new Set<string>();
  watchfaceAst.children
    .find((item) => item.tag == 'Resources')
    ?.children.forEach((item) => {
      if (item.tag == 'Image') {
        basenames.add(item.attrsMap.src);
      }
      if (item.tag == 'ImageArray') {
        item.children.forEach((item2) => {
          basenames.add(item2.attrsMap.src);
        });
      }
    });
  return basenames;
};
